import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Package, Home } from 'lucide-react';
import { orderService } from '../mock';

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const orderData = orderService.getOrderById(orderId);
    if (!orderData) {
      navigate('/');
    } else {
      setOrder(orderData);
    }
  }, [orderId, navigate]);

  if (!order) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center pb-6">
            <div className="flex justify-center mb-4">
              <div className="bg-green-100 rounded-full p-4">
                <CheckCircle className="w-16 h-16 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-3xl text-gray-900">You're on the Waitlist!</CardTitle>
            <p className="text-gray-600 mt-2">Thank you for joining the Baitho family</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-orange-50 rounded-lg p-6 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Registration ID:</span>
                <span className="font-mono font-bold text-gray-900">{order.id}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Status:</span>
                <Badge className="bg-green-100 text-green-800 border-0">Waitlist Confirmed</Badge>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">What Happens Next?</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 rounded-full p-2 flex-shrink-0 mt-1">
                    <span className="text-2xl">📧</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Confirmation Email</h4>
                    <p className="text-sm text-gray-600">
                      Check your inbox at <strong>{order.customer.email}</strong> for a confirmation email within 2-3 days
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 rounded-full p-2 flex-shrink-0 mt-1">
                    <span className="text-2xl">💳</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Payment Details</h4>
                    <p className="text-sm text-gray-600">
                      We'll share secure payment link and shipping information via email
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 rounded-full p-2 flex-shrink-0 mt-1">
                    <span className="text-2xl">📦</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Delivery in December</h4>
                    <p className="text-sm text-gray-600">
                      Your Baitho deck will be shipped in December 2025 — just in time for the holidays!
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 rounded-full p-2 flex-shrink-0 mt-1">
                    <span className="text-2xl">🎁</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Perfect Holiday Gift</h4>
                    <p className="text-sm text-gray-600">
                      Create meaningful moments with your loved ones this holiday season
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Information</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                <p className="font-medium text-gray-900">{order.customer.fullName}</p>
                <p className="text-gray-700">{order.customer.email}</p>
                <p className="text-gray-700">{order.customer.phone}</p>
                {order.customer.city && <p className="text-gray-700">📍 {order.customer.city}</p>}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                📱 Keep an eye on your phone and email. We'll reach out within 2-3 days with next steps!
              </p>
            </div>

            <div className="pt-4">
              <Button 
                size="lg" 
                className="w-full bg-mustard hover:bg-[#D4A43D] text-gray-900 font-bold"
                onClick={() => navigate('/')}
              >
                <Home className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
